// Design System Select Component
// Unified select dropdown component

import React, { forwardRef } from 'react';
import { 
  Select as MuiSelect,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  styled,
  alpha
} from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, borderTokens, transitionTokens } from '../../tokens';

// Styled FormControl wrapper
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  marginBottom: spacingTokens.component.md,
}));

// Styled Select with design system tokens
const StyledSelect = styled(MuiSelect)(({ theme }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    borderRadius: borderTokens.lg,
    backgroundColor: alpha(colors.background.paper, mode === 'dark' ? 0.5 : 0.95),
    
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(colors.divider, mode === 'dark' ? 0.4 : 0.5),
      borderWidth: 1,
      transition: transitionTokens.fast,
    },
    
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary.main,
    },
    
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary.main,
      borderWidth: 2,
    },
    
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: `${colors.error.main} !important`,
      borderWidth: 2,
    },
    
    '& .MuiSelect-select': {
      padding: `${spacingTokens.component.sm} ${spacingTokens.component.md}`,
      minHeight: '1.5em',
    },
  };
});

// Styled InputLabel
const StyledInputLabel = styled(InputLabel)(({ theme }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    color: alpha(colors.text.primary, 0.7),
    fontWeight: 500,
    
    '&.Mui-focused:not(.Mui-error)': {
      color: colors.primary.main,
      fontWeight: 600,
    },
    
    '&.Mui-error': {
      color: `${colors.error.main} !important`,
      fontWeight: 600,
    },
  };
});

/**
 * Select - Unified select dropdown component
 * 
 * Features:
 * - Design system tokens integration
 * - Consistent with TextField styling
 * - Support for multiple selection
 * - Error state handling
 * - Native or menu-based rendering
 * - Options array support for dynamic data
 * 
 * @example
 * // Basic usage with children
 * <Select label="Country" value={country} onChange={setCountry}>
 *   <MenuItem value="us">United States</MenuItem>
 *   <MenuItem value="uk">United Kingdom</MenuItem>
 * </Select>
 * 
 * // Usage with options array
 * <Select 
 *   label="Department" 
 *   value={department} 
 *   onChange={setDepartment}
 *   options={[
 *     { value: 'eng', label: 'Engineering' },
 *     { value: 'mkt', label: 'Marketing' },
 *   ]}
 *   placeholder="Select Department"
 * />
 * 
 * // Multiple selection
 * <Select label="Skills" value={skills} onChange={setSkills} multiple>
 *   <MenuItem value="react">React</MenuItem>
 *   <MenuItem value="node">Node.js</MenuItem>
 * </Select>
 */
const Select = forwardRef(({
  label,
  error,
  helperText,
  fullWidth = true,
  variant = 'outlined',
  size = 'medium',
  children,
  options,
  placeholder,
  displayEmpty,
  ...props
}, ref) => {
  // If options are provided, render MenuItems from options array
  const renderChildren = () => {
    if (options && Array.isArray(options)) {
      return options.map((option, index) => {
        // Handle different option formats
        let value, label;
        if (typeof option === 'object' && option !== null) {
          value = option.value !== undefined ? option.value : option.id;
          label = option.label !== undefined ? option.label : option.name || option.value || option.id;
        } else {
          value = option;
          label = String(option);
        }
        
        return (
          <MenuItem key={`option-${index}-${value}`} value={value}>
            {label}
          </MenuItem>
        );
      });
    }
    return children;
  };

  return (
    <StyledFormControl 
      fullWidth={fullWidth} 
      error={Boolean(error)}
      variant={variant}
      size={size}
    >
      {label && (
        <StyledInputLabel>{label}</StyledInputLabel>
      )}
      <StyledSelect
        ref={ref}
        label={label}
        displayEmpty={displayEmpty || !!placeholder}
        {...props}
        onChange={(event) => {
          // Call the onChange with just the value, not the event
          if (props.onChange) {
            props.onChange(event.target.value);
          }
        }}
      >
        {placeholder && (
          <MenuItem value="" disabled sx={{ fontStyle: 'italic', opacity: 0.6 }}>
            {placeholder}
          </MenuItem>
        )}
        {renderChildren()}
      </StyledSelect>
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </StyledFormControl>
  );
});

Select.propTypes = {
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  fullWidth: PropTypes.bool,
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  size: PropTypes.oneOf(['small', 'medium']),
  children: PropTypes.node,
  options: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    }),
    PropTypes.shape({
      id: PropTypes.any.isRequired,
      name: PropTypes.string.isRequired,
    })
  ])),
  placeholder: PropTypes.string,
  displayEmpty: PropTypes.bool,
  // All other MUI Select props are supported
};

Select.displayName = 'Select';

// Export MenuItem for convenience
export { MenuItem };
export default Select;